import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button, vars } from '@agendor/ui';
import { Modal } from './Modal';
import { FormGroup, Checkbox, CheckboxLabel, HelperText, WarningBanner } from './Input';
import { OpeningHoursEditor } from './OpeningHoursEditor';
import {
    OpeningHoursDraft,
    PlainOpeningHours,
    emptyDraft,
    fromDraft,
    summarizeOpeningHours,
    toDraft,
} from '../opening-hours';

const WorkerHoursSummary = styled.div`
    font-weight: 400;
    font-size: 0.95rem;
    color: ${vars.foregroundColor};
    display: flex;
    flex-direction: column;
    gap: 2px;
    font-variant-numeric: tabular-nums;
`;

const CopyHoursRow = styled.div`
    display: flex;
    justify-content: flex-end;
`;

export function summarizeWorkerOpeningHours(openingHours: PlainOpeningHours): string[] {
    return summarizeOpeningHours(toDraft(openingHours), { closedLabel: 'Não atende' });
}

/** The "Horário personalizado" toggle, plus the summary and the way into the editor while it is on. */
export function WorkerOpeningHoursField({
    openingHours,
    onToggle,
    onConfigure,
    disabled,
}: {
    openingHours: PlainOpeningHours | null;
    onToggle: (enabled: boolean) => void;
    onConfigure: () => void;
    disabled: boolean;
}) {
    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <CheckboxLabel>
                    <Checkbox
                        data-testid="worker-custom-hours-checkbox"
                        checked={openingHours !== null}
                        onChange={(e) => onToggle(e.target.checked)}
                        disabled={disabled}
                    />
                    Horário personalizado
                </CheckboxLabel>
                <HelperText>
                    Restringe os dias e horários de atendimento, sempre dentro do horário do estabelecimento.
                </HelperText>
            </div>
            {openingHours !== null && (
                <>
                    <WorkerHoursSummary data-testid="worker-hours-summary">
                        {summarizeWorkerOpeningHours(openingHours).map((line) => (
                            <span key={line}>{line}</span>
                        ))}
                    </WorkerHoursSummary>
                    <div style={{ display: 'flex', marginTop: '8px' }}>
                        <Button
                            type="button"
                            data-testid="worker-hours-configure-btn"
                            onClick={onConfigure}
                            disabled={disabled}
                        >
                            Configurar horário
                        </Button>
                    </div>
                </>
            )}
        </>
    );
}

/**
 * Edits a professional's weekly grid. It opens on their own grid, or on a copy of the
 * establishment's for one who has none yet.
 */
export function WorkerOpeningHoursModal({
    isOpen,
    title,
    workerOpeningHours,
    businessOpeningHours,
    isSaving,
    onClose,
    onCopyBusinessHours,
    onSave,
}: {
    isOpen: boolean;
    title: string;
    workerOpeningHours: PlainOpeningHours | null;
    businessOpeningHours: PlainOpeningHours;
    isSaving: boolean;
    onClose: () => void;
    onCopyBusinessHours: () => void;
    onSave: (openingHours: PlainOpeningHours) => void;
}) {
    const [draft, setDraft] = useState<OpeningHoursDraft>(emptyDraft());

    useEffect(() => {
        if (!isOpen) return;
        setDraft(toDraft(workerOpeningHours ?? businessOpeningHours));
    }, [isOpen, workerOpeningHours, businessOpeningHours]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            onBack={onClose}
            title={title}
            data-testid="worker-hours-modal"
            footer={
                <>
                    <Button data-testid="modal-cancel" onClick={onClose} disabled={isSaving}>
                        Cancelar
                    </Button>
                    <Button
                        primary
                        data-testid="worker-hours-save-btn"
                        onClick={() => onSave(fromDraft(draft))}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </>
            }
        >
            <FormGroup>
                <WarningBanner $severity="info">
                    O horário personalizado vale apenas dentro do horário de funcionamento do estabelecimento. Fora
                    dele, o profissional não recebe agendamentos.
                </WarningBanner>
                <CopyHoursRow>
                    <Button
                        type="button"
                        data-testid="worker-hours-copy-btn"
                        onClick={() => {
                            onCopyBusinessHours();
                            setDraft(toDraft(businessOpeningHours));
                        }}
                        disabled={isSaving}
                    >
                        Copiar horário do estabelecimento
                    </Button>
                </CopyHoursRow>
                <OpeningHoursEditor
                    value={draft}
                    onChange={setDraft}
                    disabled={isSaving}
                    reference={toDraft(businessOpeningHours)}
                    closedLabel="Não atende"
                />
            </FormGroup>
        </Modal>
    );
}
